import { createContext, useContext, useReducer } from "react";

const COMPOSER_STATUS = {
  CLOSED: "closed",
  OPEN: "open",
  MINIMIZED: "minimized",
};

const initialState = {
  status: COMPOSER_STATUS.CLOSED,
  to: [],
  cc: [],
  subject: "",
  body: "",
  attachments: [],
  contactId: null,
  projectId: null,
  contact: null,
  project: null,
  advisor: null,
  organization: null,
};

const ACTIONS = {
  OPEN: "OPEN",
  CLOSE: "CLOSE",
  MINIMIZE: "MINIMIZE",
  RESTORE: "RESTORE",
  SET_SUBJECT: "SET_SUBJECT",
  SET_BODY: "SET_BODY",
  SET_CC: "SET_CC",
  ADD_ATTACHMENT: "ADD_ATTACHMENT",
  REMOVE_ATTACHMENT: "REMOVE_ATTACHMENT",
  APPLY_TEMPLATE: "APPLY_TEMPLATE",
};

const reducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.OPEN:
      return {
        ...initialState,
        status: COMPOSER_STATUS.OPEN,
        to: action.payload.to || [],
        contactId: action.payload.contactId,
        projectId: action.payload.projectId,
        contact: action.payload.contact,
        project: action.payload.project,
        advisor: action.payload.advisor,
        organization: action.payload.organization,
      };
    case ACTIONS.CLOSE:
      return { ...initialState };
    case ACTIONS.MINIMIZE:
      return { ...state, status: COMPOSER_STATUS.MINIMIZED };
    case ACTIONS.RESTORE:
      return { ...state, status: COMPOSER_STATUS.OPEN };
    case ACTIONS.SET_SUBJECT:
      return { ...state, subject: action.payload };
    case ACTIONS.SET_BODY:
      return { ...state, body: action.payload };
    case ACTIONS.SET_CC:
      return { ...state, cc: action.payload };
    case ACTIONS.ADD_ATTACHMENT:
      return {
        ...state,
        attachments: [...state.attachments, action.payload],
      };
    case ACTIONS.REMOVE_ATTACHMENT:
      return {
        ...state,
        attachments: state.attachments.filter(
          (_, idx) => idx !== action.payload,
        ),
      };
    case ACTIONS.APPLY_TEMPLATE:
      return {
        ...state,
        subject: action.payload.subject || state.subject,
        body: action.payload.body || state.body,
      };
    default:
      return state;
  }
};

const EmailComposerContext = createContext(null);

const EmailComposerProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const openComposer = ({ contact, project, advisor, organization }) => {
    const emails = (contact?.telecoms || [])
      .filter((t) => t.system === "EMAIL")
      .map((t) => t.value)
      .filter(Boolean);

    dispatch({
      type: ACTIONS.OPEN,
      payload: {
        to: emails,
        contactId: contact?.id,
        projectId: project?.id,
        contact,
        project,
        advisor,
        organization,
      },
    });
  };

  const closeComposer = () => dispatch({ type: ACTIONS.CLOSE });
  const minimizeComposer = () => dispatch({ type: ACTIONS.MINIMIZE });
  const restoreComposer = () => dispatch({ type: ACTIONS.RESTORE });
  const setSubject = (value) =>
    dispatch({ type: ACTIONS.SET_SUBJECT, payload: value });
  const setBody = (value) =>
    dispatch({ type: ACTIONS.SET_BODY, payload: value });
  const setCc = (value) => dispatch({ type: ACTIONS.SET_CC, payload: value });
  const addAttachment = (attachment) =>
    dispatch({ type: ACTIONS.ADD_ATTACHMENT, payload: attachment });
  const removeAttachment = (index) =>
    dispatch({ type: ACTIONS.REMOVE_ATTACHMENT, payload: index });
  const applyTemplate = ({ subject, body }) =>
    dispatch({
      type: ACTIONS.APPLY_TEMPLATE,
      payload: { subject, body },
    });

  return (
    <EmailComposerContext.Provider
      value={{
        ...state,
        openComposer,
        closeComposer,
        minimizeComposer,
        restoreComposer,
        setSubject,
        setBody,
        setCc,
        addAttachment,
        removeAttachment,
        applyTemplate,
      }}
    >
      {children}
    </EmailComposerContext.Provider>
  );
};

const useEmailComposer = () => {
  const context = useContext(EmailComposerContext);
  if (!context) {
    throw new Error(
      "useEmailComposer must be used within EmailComposerProvider",
    );
  }
  return context;
};

export { COMPOSER_STATUS, EmailComposerProvider, useEmailComposer };

export default EmailComposerContext;
